import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchAuctionCompaniesAPI,
  getRefreshTokenAPI,
  loginUserAPI,
  getFeatureFlagsAPI,
} from "../services/authAPI";
import { persistor } from "../store";
import { RootState } from "../store";
import localforage from "localforage";
import { removeWorkOrderDetail } from "./workOrderDetailSlice";
import { removeInspectionTemplates } from "./inspectionTemplatesSlice";
import { removeWorkOrder } from "./workOrderSlice";
import { hideToaster } from "./toasterSlice";
import { jwtDecode } from "jwt-decode";
import * as Sentry from "@sentry/react";
import { updateIndexDBSettings } from "../../db";
import { isReallyOnline } from "../../lib/isOnline";
import { isOfflineMode } from "../../shared/offlineManager";

export interface AuthState {
  token: string | null;
  userId: string | null;
  userName: string | null;
  refreshToken: string | null;
  tenant: string;
  tenantKey: string;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  auctionCompanies: { value: string; label: string }[];
  featureFlags: Record<string, boolean>;
}

const initialState: AuthState = {
  token: null,
  refreshToken: null,
  tenant: "",
  tenantKey: "",
  isAuthenticated: false,
  loading: false,
  error: null,
  auctionCompanies: [],
  userId: null,
  userName: null,
  featureFlags: {},
};

export const fetchAuctionCompanies = createAsyncThunk(
  "auth/fetchAuctionCompanies",
  async (email: string, { rejectWithValue }) => {
    try {
      const auctionCompanies = await fetchAuctionCompaniesAPI(email);
      return auctionCompanies;
    } catch (error: any) {
      console.log("[Error in Fetching Auction Companies]:: ", error);
      return rejectWithValue(
        error.message || "Failed to fetch auction companies"
      );
    }
  }
);

export const logoutAndClearStorage = createAsyncThunk(
  "auth/logoutAndClearStorage",
  async (_, { dispatch }) => {
    dispatch(logout());

    dispatch(removeInspectionTemplates());
    dispatch(removeWorkOrderDetail());
    dispatch(removeWorkOrder());
    dispatch(hideToaster());
    await localStorage.clear();
    await localforage.clear();
    await persistor.purge();
  }
);

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (
    {
      email,
      password,
      auctionCompany,
    }: { email: string; password: string; auctionCompany: { tenantKey: string; tenantName: string } },
    { rejectWithValue }
  ) => {
    try {
      const { token, refreshToken, userName, userId } = await loginUserAPI(
        email,
        password,
        auctionCompany.tenantName
      );
      return { token, auctionCompany, refreshToken, userName, userId };
    } catch (error: any) {
      console.log("[Error in Login]:: ", error);
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

export const checkToken = createAsyncThunk(
  "auth/checkToken",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const token = state.auth.token;
      if (token) {
        const decoded: any = jwtDecode(token);

        // Calculate time until expiration in milliseconds
        const timeUntilExpiry = decoded.exp * 1000 - Date.now();
        // Refresh if token is expired OR will expire in the next 5 minutes
        if (timeUntilExpiry < 5 * 60 * 1000) {
          const refreshToken = state.auth.refreshToken;
          const tenant = state.auth.tenant;
          if (refreshToken && tenant) {
            console.log("Refreshing token");
            try {
              await dispatch(
                getRefreshToken({ RefreshToken: refreshToken, Tenant: tenant })
              );
            } catch (refreshError) {
              // If refresh fails, log the user out
              dispatch(logoutAndClearStorage());
              throw refreshError;
            }
          } else {
            // If we don't have refresh token or tenant, log out
            dispatch(logoutAndClearStorage());
            throw new Error("RefreshToken or Tenant is missing.");
          }
        }
      }
    } catch (error: any) {
      console.log("[Error in checkToken]:: ", error);
      return rejectWithValue(error.message || "Token validation failed");
    }
  }
);

export const getRefreshToken = createAsyncThunk(
  "auth/getRefreshToken",
  async (
    { RefreshToken, Tenant }: { RefreshToken: string; Tenant: string },
    { rejectWithValue }
  ) => {
    try {
      const { token, refreshToken } = await getRefreshTokenAPI(
        RefreshToken,
        Tenant
      );
      return { token, refreshToken };
    } catch (error: any) {
      console.log("[Error in Login]:: ", error);
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

export const fetchFeatureFlags = createAsyncThunk(
  "auth/fetchFeatureFlags",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;
      const currentFeatureFlags = state.auth.featureFlags || {};

      // Check if we're online and have a token
      const isOnline = await isReallyOnline();
      const isManualOffline = isOfflineMode();

      if (!isOnline || isManualOffline || !token) {
        // Offline - return cached feature flags (don't reject)
        console.log("Offline or no token - returning cached feature flags");
        return currentFeatureFlags;
      }

      // Online - fetch from API
      const response = await getFeatureFlagsAPI();

      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(
          response.data.message || "Failed to fetch feature flags"
        );
      }
    } catch (error: any) {
      console.log("[Error in Fetching Feature Flags]:: ", error);

      // If offline error, return cached flags instead of rejecting
      const state = getState() as RootState;
      const currentFeatureFlags = state.auth.featureFlags || {};

      // Check if it's a network error (likely offline)
      if (
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("NetworkError") ||
        error.code === "ERR_NETWORK"
      ) {
        console.log("Network error - returning cached feature flags");
        return currentFeatureFlags;
      }

      return rejectWithValue(error.message || "Failed to fetch feature flags");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.token = null;
      state.refreshToken = null;
      state.tenant = "";
      state.tenantKey = "";
      state.isAuthenticated = false;
      state.error = null;
      state.featureFlags = {};

      // Clear token, userId, tenant, and tenantKey from IndexedDB
      updateIndexDBSettings("authToken", null).catch((err) => {
        console.error("Failed to clear auth token from IndexedDB:", err);
      });
      updateIndexDBSettings("userId", null).catch((err) => {
        console.error("Failed to clear userId from IndexedDB:", err);
      });
      updateIndexDBSettings("tenant", null).catch((err) => {
        console.error("Failed to clear tenant from IndexedDB:", err);
      });
      updateIndexDBSettings("tenantKey", null).catch((err) => {
        console.error("Failed to clear tenantKey from IndexedDB:", err);
      });
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuctionCompanies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAuctionCompanies.fulfilled,
        (state, action: PayloadAction<{ value: string; label: string }[]>) => {
          state.loading = false;
          state.auctionCompanies = action.payload;
        }
      )
      .addCase(fetchAuctionCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        loginUser.fulfilled,
        (
          state,
          action: PayloadAction<{
            token: string;
            refreshToken: string;
            auctionCompany: { tenantName: string; tenantKey: string };
            userId: string;
            userName: string;
          }>
        ) => {
          state.loading = false;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.tenant = action.payload.auctionCompany.tenantName;
          state.tenantKey = action.payload.auctionCompany.tenantKey;
          state.isAuthenticated = true;
          state.userId = action.payload.userId;
          state.userName = action.payload.userName;

          // Store token, userId, tenant, and tenantKey in IndexedDB for service worker access
          updateIndexDBSettings("authToken", action.payload.token).catch(
            (err) => {
              console.error("Failed to store auth token in IndexedDB:", err);
            }
          );
          updateIndexDBSettings("userId", action.payload.userId).catch(
            (err) => {
              console.error("Failed to store userId in IndexedDB:", err);
            }
          );
          updateIndexDBSettings("tenant", action.payload.auctionCompany.tenantName).catch(
            (err) => {
              console.error("Failed to store tenant in IndexedDB:", err);
            }
          );
          updateIndexDBSettings("tenantKey", action.payload.auctionCompany.tenantKey).catch(
            (err) => {
              console.error("Failed to store tenantKey in IndexedDB:", err);
            }
          );

          Sentry.setContext("loginEvent", {
            timestamp: new Date().toISOString(),
            userId: action.payload.userId,
            tenant: action.payload.auctionCompany.tenantName,
            userName: action.payload.userName,
          });
        }
      )
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(
        getRefreshToken.fulfilled,
        (
          state,
          action: PayloadAction<{ token: string; refreshToken: string }>
        ) => {
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;

          // Update token in IndexedDB for service worker access
          updateIndexDBSettings("authToken", action.payload.token).catch(
            (err) => {
              console.error("Failed to update auth token in IndexedDB:", err);
            }
          );
        }
      )
      .addCase(getRefreshToken.rejected, (state) => {
        // If token refresh fails, we should log the user out
        state.token = null;
        state.refreshToken = null;
        state.tenant = "";
        state.tenantKey = "";
        state.isAuthenticated = false;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action) => {
        state.featureFlags = action.payload || {};
      })
      .addCase(fetchFeatureFlags.rejected, (_, action) => {
        console.error("Failed to fetch feature flags:", action.payload);
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
